import React, { useState } from 'react';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { APIProvider, Map, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import CustomAdvancedMarker from '../common/CustomAdvancedMarker';
import SingleAdvancedMarker from '../common/SingleAdvancedMarket';
import Select from 'react-select';

function Directions({ routeData: { origin, destination, price, leaseData, pathColor = 'blue' },
    allRoutesCalculation = [], showRoutesData, setShowRoutesData, truckingRates }) {
    const map = useMap();
    const routesLibrary = useMapsLibrary('routes');
    const [directionsService, setDirectionsService] = React.useState();
    const [directionsRenderer, setDirectionsRenderer] = React.useState();
    const [routes, setRoutes] = React.useState([]);
    const [routeIndex, setRouteIndex] = React.useState(0);
    const [selectedCarrier, setSelectedCarrier] = React.useState();
    const [carrierSelectOptions, setCarrierSelectOptions] = React.useState([]);
    const [routesRatesData, setRoutesRatesData] = useState([])
    const [selectedCarrierInfoByStation, setSelectedCarrierInfoByStation] = React.useState({});
    
    const selected = routes[routeIndex];
    const leg = selected?.legs[0];

    React.useEffect(() => {
        if (!routesLibrary || !map) return;
        setDirectionsService(new routesLibrary.DirectionsService());
        setDirectionsRenderer(new routesLibrary.DirectionsRenderer({
            map,
            suppressInfoWindows: true,
            suppressMarkers: true,
            suppressPolylines: false,
            options: {
                preserveViewport: true,
                polylineOptions: {
                    strokeColor: pathColor
                }
            }
        }));
    }, [routesLibrary, map]);

    React.useEffect(() => {
        if (!directionsService || !directionsRenderer) return;

        directionsService
            .route({
                travelMode: "DRIVING",
                origin,
                destination,
                provideRouteAlternatives: true
            })
            .then(response => {
                directionsRenderer.setDirections(response);
                setRoutes(response.routes);
            });

        return () => directionsRenderer.setMap(null);
    }, [directionsService, directionsRenderer, origin, destination]);


    const getAllRates = (carriers) => {
        const rates = allRoutesCalculation.map(route => (
            carriers.map(carrier => {
                const rate = truckingRates[carrier].find(truckingRate => truckingRate.mileage === Math.ceil(route.distance))?.rate
                    || truckingRates[carrier][truckingRates[carrier].length - 1].rate
    
                return {
                    carrier,
                    rate,
                    station: route.station,
                }
            })
        ))

        setRoutesRatesData(rates)
    }

    React.useEffect(() => {
        if (!directionsRenderer) return;
        directionsRenderer.setRouteIndex(routeIndex);
        if (truckingRates) {
            const carriers = Object.keys(truckingRates)
            const carriersOptions = carriers.map(carrier => ({ value: carrier, label: carrier.charAt(0).toUpperCase() + carrier.slice(1).toLowerCase() }))
            setCarrierSelectOptions(carriersOptions)
            getAllRates(carriers)
        }

    }, [routeIndex, directionsRenderer, truckingRates]);

    if (!leg) return null;

    let getRates
    if (truckingRates) {
        const carriers = Object.keys(truckingRates)
        getRates = (distance) => {
            return carriers.map(carrier => {
                const rate = truckingRates[carrier].find(truckingRate => truckingRate.mileage === Math.ceil(distance))?.rate
                    || truckingRates[carrier][truckingRates[carrier].length - 1].rate

                return {
                    carrier,
                    rate,
                }
            }
            )
        }
    }

    const handleSelectCarrier = event => {
        const carrier = event.value
        const selectedCarrierInfo = routesRatesData.map(route => route.filter(rate => rate.carrier === carrier))
        const selectedCarrierInfoByStation = selectedCarrierInfo.reduce((acc, info) => {
            const {station, ...rest} = info[0]
            acc[station] = rest
            return acc
        }, {}) 
        setSelectedCarrierInfoByStation(selectedCarrierInfoByStation)


        setSelectedCarrier(carrier)
    }

    const getRoutePrice = route => {
        let stationPrice
        if(route.price.bsw) {
            const stationBswPrice = route.price.bsw.find(bswData => (
                Number(bswData.low) <= Number(leaseData.bsw) &&
                Number(bswData.high) >= Number(leaseData.bsw)   
            ))

            stationPrice = stationBswPrice?.value
        } else if(route.price.gravity) {
            const stationGravityPrice = route.price.gravity.find(gravityData => (
                Number(gravityData.low) <= Number(leaseData.api) &&
                Number(gravityData.high) >= Number(leaseData.api)   
            ))

            stationPrice = stationGravityPrice?.value
        } else {
            stationPrice = route.price.value
        }

        const rate = selectedCarrierInfoByStation[route.station].rate
        const price = (Number(stationPrice) - Number(rate))
        return price || 'No price set for this station'
    }

    return (
        <>
            {
                allRoutesCalculation.length > 0 ? (
                    <div className="directions">
                        <div style={{ display: 'flex', cursor: 'pointer' }} onClick={() => setShowRoutesData(!showRoutesData)}>
                            <h1 style={{ textAlign: 'center', fontSize: 20 }}>Routes within 300 miles</h1>
                            <span style={{ marginLeft: '10px', marginTop: '7px' }}>
                                <FontAwesomeIcon icon={showRoutesData ? faCaretUp : faCaretDown} />
                            </span>
                        </div>
                        <Select
                              styles={{
                                control: (baseStyles, state) => ({
                                  ...baseStyles,
                                  backgroundColor: 'white',
                                }),
                                option: (baseStyles, { isFocused}) => ({
                                    ...baseStyles,
                                    backgroundColor: isFocused ? 'lightblue' : 'white',
                                    color: 'black'
                                  }),
                              }}
                            options={carrierSelectOptions}
                            isSearchable
                            placeholder="Select carrier"
                            onChange={handleSelectCarrier}
                        />

                        {
                            showRoutesData && (
                                <ul style={{ listStyleType: "disc", marginTop: 5 }}>
                                    {allRoutesCalculation.map(route => (
                                        <li key={route.distance}>
                                            <span style={{ color: route.pathColor }}><strong>{route.station} - {route.market}:</strong></span>
                                            <br />- Distance: {route.distance} miles
                                            <br />- Time: {route.duration}
                                            {
                                                selectedCarrierInfoByStation[route.station] && (
                                                    <>
                                                        <br />- Rate: {selectedCarrierInfoByStation[route.station].rate}
                                                        <br />- Price: {getRoutePrice(route)}
                                                    </>
                                                )
                                            }
                                        </li>
                                    ))}
                                </ul>
                            )
                        }
                    </div>
                ) : (
                    <div className="directions">
                        <div style={{ display: 'flex', cursor: 'pointer' }} onClick={() => setShowRoutesData(!showRoutesData)}>
                            <h1 style={{ textAlign: 'center', fontSize: 20 }}>Route Calculation</h1>
                            <span style={{ marginLeft: '7px', marginTop: '4px' }}>
                                <FontAwesomeIcon icon={showRoutesData ? faCaretUp : faCaretDown} />
                            </span>
                        </div>
                        {
                            showRoutesData && (
                                <div>
                                    <h2 style={{ fontWeight: 'bold' }}>{selected.summary}</h2>
                                    <p>- From {leg.start_address.split(',')[0]} to {leg.end_address.split(',')[0]}</p>
                                    <p>- Distance: {leg.distance?.text}</p>
                                    <p>- Duration: {leg.duration?.text}</p>
                                    - Rates:
                                    <ul style={{ listStyleType: "circle", marginLeft: 10 }}>
                                        {
                                            getRates(leg.distance?.text.split(' mi')[0]).map((rateData, index) => (
                                                <li key={index}>
                                                    {rateData.carrier.charAt(0).toUpperCase() + rateData.carrier.slice(1).toLowerCase()}: {rateData.rate}
                                                </li>

                                            ))
                                        }
                                    </ul>
                                    <h2>- Other Routes</h2>
                                    <ul style={{ listStyleType: "disc" }}>
                                        {routes.map((route, index) => (
                                            <li key={route.summary}>
                                                <button style={{ textDecoration: "underline" }} onClick={() => setRouteIndex(index)}>
                                                    {route.summary}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>)
                        }
                    </div>


                )
            }
        </>
    );
}

const MapComponent = ({
    cameraProps,
    handleCameraChange,
    mapMarkerData,
    isClustered,
    openWindowSelectedCoordenate,
    openWindowSelectedMarketCoordenate,
    setOpenWindowSelectedMarketCoordenate,
    setOpenWindowSelectedCoordenate,
    randomCoordenateMapMarkerData,
    secondRandomCoordenateMapMarkerData,
    routeCalculation,
    routeCalculations,
    isLoadingZoomCamera,
    truckingRates
}) => {

    const [closeInfo, setCloseInfo] = useState(false)
    const [showRoutesData, setShowRoutesData] = useState(true)

    return (
        <div style={{ width: '85%' }}>
            {
                isLoadingZoomCamera && (
                    <div className='loading'>
                        <div className="spinner"></div>;
                    </div>
                )
            }
            <APIProvider apiKey={"AIzaSyArQc3VJ5UufQwcoWIRUQIbdrVQ-UlhaRc"} language='en'>
                <Map
                    mapId={'bf51a910020fa25a'}
                    {...cameraProps}
                    onCameraChanged={handleCameraChange}
                    gestureHandling={'greedy'}
                    disableDefaultUI
                    onClick={() => setCloseInfo(true)}
                >
                    {mapMarkerData?.length > 0 && setOpenWindowSelectedMarketCoordenate && setOpenWindowSelectedCoordenate && (
                        <CustomAdvancedMarker
                            isClustered={isClustered}
                            points={mapMarkerData}
                            closeInfo={closeInfo}
                            handleInfoClose={() => setCloseInfo(false)}
                            openPoint={openWindowSelectedCoordenate}
                            openMarketPoint={openWindowSelectedMarketCoordenate}
                            setOpenWindowSelectedMarketCoordenate={setOpenWindowSelectedMarketCoordenate}
                            setOpenWindowSelectedCoordenate={setOpenWindowSelectedCoordenate}
                            routeCalculation={routeCalculation}
                        />
                    )}

                    <SingleAdvancedMarker
                        isClustered={false}
                        points={randomCoordenateMapMarkerData}
                        closeInfo={closeInfo}
                        handleInfoClose={() => setCloseInfo(false)}
                        openPoint={openWindowSelectedCoordenate}
                    />

                    <SingleAdvancedMarker
                        isClustered={false}
                        points={secondRandomCoordenateMapMarkerData}
                        closeInfo={closeInfo}
                        handleInfoClose={() => setCloseInfo(false)}
                        openPoint={openWindowSelectedCoordenate}
                    />

                    {routeCalculation &&
                        <Directions
                            routeData={routeCalculation}
                            truckingRates={truckingRates}
                            showRoutesData={showRoutesData}
                            setShowRoutesData={setShowRoutesData}
                        />}

                    {
                        routeCalculations && routeCalculations.map((routeCalculationItem, index) => (
                            <Directions
                                key={index}
                                routeData={routeCalculationItem}
                                allRoutesCalculation={routeCalculations}
                                showRoutesData={showRoutesData}
                                truckingRates={truckingRates}
                                setShowRoutesData={setShowRoutesData} />
                        ))
                    }
                </Map>
            </APIProvider>
        </div>
    );
};

export default MapComponent;